import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Settings, ChevronDown, ChevronUp } from "lucide-react";

const API = "/api";

export const StrategySettings = ({ onSettingsChange }) => {
  const [settings, setSettings] = useState(null);
  const [showPro, setShowPro] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/strategy/settings`);
      setSettings(res.data);
    } catch (e) {
      console.error("Error fetching settings:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSetting = async (key, value) => {
    try {
      const params = new URLSearchParams();
      params.append(key, value.toString());
      await axios.post(`${API}/strategy/settings?${params.toString()}`);
      setSettings(prev => ({ ...prev, [key]: value }));
      if (onSettingsChange) onSettingsChange();
      toast.success(`${key.replace(/_/g, " ")} updated`);
    } catch (e) {
      toast.error("Failed to update setting");
    }
  };

  if (loading || !settings) {
    return <div className="text-xs text-zinc-500">Loading settings...</div>;
  }

  return (
    <div className="space-y-6" data-testid="strategy-settings">
      {/* Trade Frequency Mode */}
      <div className="space-y-3">
        <Label className="text-xs text-zinc-500 uppercase tracking-wider">Trade Frequency</Label>
        <div className="grid grid-cols-3 gap-1">
          {["conservative", "normal", "active"].map((mode) => (
            <button
              key={mode}
              onClick={() => updateSetting("trade_frequency_mode", mode)}
              className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-colors ${
                settings.trade_frequency_mode === mode
                  ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
              data-testid={`freq-${mode}`}
            >
              {mode}
            </button>
          ))}
        </div>
        <p className="text-xs text-zinc-600">
          {settings.trade_frequency_mode === "conservative" && "One trade per higher-timeframe swing"}
          {settings.trade_frequency_mode === "normal" && "One trade per setup, re-entry after exit"}
          {settings.trade_frequency_mode === "active" && "Same-direction re-entries and scale-ins allowed"}
        </p>
      </div>

      {/* Redraw Mode */}
      <div className="space-y-3">
        <Label className="text-xs text-zinc-500 uppercase tracking-wider">Fib Redraw Mode</Label>
        <div className="grid grid-cols-2 gap-1">
          {["confirmed", "aggressive"].map((mode) => (
            <button
              key={mode}
              onClick={() => updateSetting("redraw_mode", mode)}
              className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-colors ${
                settings.redraw_mode === mode
                  ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
              data-testid={`redraw-${mode}`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Swing Strength */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-zinc-500 uppercase tracking-wider">Swing Strength</Label>
          <span className="text-xs font-mono text-zinc-400">{settings.swing_strength} bars</span>
        </div>
        <Slider
          value={[settings.swing_strength]}
          onValueChange={([v]) => updateSetting("swing_strength", v)}
          min={1}
          max={10}
          step={1}
          className="py-2"
        />
        <p className="text-xs text-zinc-600">Higher = fewer redraws, cleaner structure</p>
      </div>

      {/* Trailing Stop Mode */}
      <div className="space-y-3">
        <Label className="text-xs text-zinc-500 uppercase tracking-wider">Trailing Stop Mode</Label>
        <div className="grid grid-cols-3 gap-1">
          {["fib", "structure", "atr"].map((mode) => (
            <button
              key={mode}
              onClick={() => updateSetting("trailing_stop_mode", mode)}
              className={`px-3 py-2 text-xs font-mono uppercase rounded border transition-colors ${
                settings.trailing_stop_mode === mode
                  ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
              }`}
              data-testid={`trail-${mode}`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Pyramiding Toggle */}
      <div className="flex items-center justify-between py-3 px-4 bg-zinc-900 rounded border border-zinc-800">
        <div>
          <div className="text-xs font-medium">Pyramiding</div>
          <div className="text-xs text-zinc-500">Profit-funded adds only</div>
        </div>
        <Switch
          checked={settings.pyramiding_enabled}
          onCheckedChange={(v) => updateSetting("pyramiding_enabled", v)}
          data-testid="pyramiding-toggle"
        />
      </div>

      {/* Pro Toggles */}
      <div className="border-t border-zinc-800 pt-4">
        <button
          onClick={() => setShowPro(!showPro)}
          className="flex items-center justify-between w-full text-xs text-zinc-500 uppercase tracking-wider mb-3"
        >
          <span>Pro Toggles</span>
          {showPro ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        
        {showPro && (
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded">
              <span className="text-xs text-zinc-400">1m Entry Precision</span>
              <Switch
                checked={settings.pro_1m_entry}
                onCheckedChange={(v) => updateSetting("pro_1m_entry", v)}
                data-testid="pro-1m-toggle"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded">
              <span className="text-xs text-zinc-400">Aggressive Redraw</span>
              <Switch
                checked={settings.pro_aggressive_redraw}
                onCheckedChange={(v) => updateSetting("pro_aggressive_redraw", v)}
                data-testid="pro-redraw-toggle"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded">
              <span className="text-xs text-zinc-400">High Frequency Mode</span>
              <Switch
                checked={settings.pro_high_frequency}
                onCheckedChange={(v) => updateSetting("pro_high_frequency", v)}
                data-testid="pro-freq-toggle"
              />
            </div>
            <div className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded">
              <span className="text-xs text-zinc-400">Live Anchor Tracking</span>
              <Switch
                checked={settings.pro_live_anchor}
                onCheckedChange={(v) => updateSetting("pro_live_anchor", v)}
                data-testid="pro-anchor-toggle"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
